const nodeFetch = require("node-fetch");

const universalFetch = (function getCorrectFetchImpl() {
  try {
    return fetch;
  } catch (ex) {
    return nodeFetch;
  }
})();

// 不跳轉的 API 路徑 - 這些 API 404 錯誤不會觸發頁面跳轉
const NO_REDIRECT_API_PATHS = ["/api/promotion/feedback/"];

function addTailingSlashForPath(url) {
  let [path, query] = url.split("?");
  if (path[path.length - 1] !== "/") {
    path += "/";
  }
  return `${path}${query ? "?" + query : ""}`;
}

// 檢查是否為不跳轉的 API
const isNoRedirectAPI = (path) => {
  if (!path) return false;
  return NO_REDIRECT_API_PATHS.some((noRedirectPath) =>
    path.includes(noRedirectPath)
  );
};

// 全域錯誤處理函數
const handleGlobalApiError = (error, status, path) => {
  if (typeof window !== "undefined" && !isNoRedirectAPI(path)) {
    if (status === 404) {
      // 避免重複重定向
      if (window.location.pathname !== "/404") {
        window.location.href = "/404";
      }
    } else if (status === 500) {
      if (window.location.pathname !== "/500") {
        console.error("500 error", error);
      }
    }
  }
};

class _ApiProxy {
  constructor({ apiUrl }) {
    this._commonHeader = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };

    this.apiUrl = apiUrl;
    this.apiToken = null;
    this.enableGlobalErrorHandling = true; // 全域錯誤處理
  }

  // 設置是否啟用全域錯誤處理
  setGlobalErrorHandling(enabled) {
    this.enableGlobalErrorHandling = enabled;
  }

  _buildEndpoint({ path, withHost = false }) {
    if (withHost) {
      return path;
    }

    // `withHost` means we're sending req to our own API server,
    // which is DRF based and we hope to see tailing slash
    // path = addTailingSlashForPath(path);
    return path ? `${this.apiUrl}${path}` : null;
  }

  _buildHeaders({ extraHeaders, skipCommonHeader = false, secure = true }) {
    let authHeader =
      this.apiToken && secure
        ? { Authorization: `Token ${this.apiToken}` }
        : {};
    if (skipCommonHeader) {
      return authHeader;
    }
    return {
      ...this._commonHeader,
      ...authHeader,
    };
  }

  setToken(token) {
    this.apiToken = token;
  }

  get({ path, extraHeaders = {}, withHost = false, secure = true }) {
    return universalFetch(this._buildEndpoint({ path, withHost }), {
      method: "GET",
      headers: this._buildHeaders({ extraHeaders, secure }),
    }).then(async (response) => {
      if (response.status === 204) {
        return null;
      }

      if (`${response.status}`[0] === "2") {
        return response.json();
      }

      let error = await response.json();

      // 全域錯誤處理
      if (this.enableGlobalErrorHandling) {
        handleGlobalApiError(error, response.status, path);
      }

      return Promise.reject({ status: response.status, ...error });
    });
  }

  post({ path, extraHeaders = {}, data, withHost = false, secure = true }) {
    return universalFetch(this._buildEndpoint({ path, withHost }), {
      method: "POST",
      headers: this._buildHeaders({ extraHeaders, secure }),
      body: JSON.stringify(data),
    }).then(async (response) => {
      if (response.status === 204) {
        return null;
      }

      if (`${response.status}`[0] === "2") {
        return response.json();
      }

      let error = await response.json();
      console.log("error", error);

      // 全域錯誤處理
      if (this.enableGlobalErrorHandling) {
        handleGlobalApiError(error, response.status, path);
      }

      return Promise.reject({ status: response.status, ...error });
    });
  }

  put({ path, extraHeaders = {}, data, withHost = false, secure = true }) {
    return universalFetch(this._buildEndpoint({ path, withHost }), {
      method: "PUT",
      headers: this._buildHeaders({ extraHeaders, secure }),
      body: JSON.stringify(data),
    }).then(async (response) => {
      if (response.status === 204) {
        return null;
      }

      if (`${response.status}`[0] === "2") {
        return response.json();
      }

      let error = await response.json();

      // 全域錯誤處理
      if (this.enableGlobalErrorHandling) {
        handleGlobalApiError(error, response.status, path);
      }

      return Promise.reject({ status: response.status, ...error });
    });
  }

  delete({ path, extraHeaders = {}, withHost = false, secure = true }) {
    const url = this._buildEndpoint({ path, withHost });
    return universalFetch(url, {
      method: "DELETE",
      headers: this._buildHeaders({ extraHeaders, secure }),
    }).then(async (response) => {
      if (response.status === 204) {
        return null;
      }

      if (`${response.status}`[0] === "2") {
        return response.json();
      }

      let error = await response.json();

      // 全域錯誤處理
      if (this.enableGlobalErrorHandling) {
        handleGlobalApiError(error, response.status, path);
      }

      return Promise.reject({ status: response.status, ...error });
    });
  }

  formPost({ path, extraHeaders = {}, formData, withHost = false }) {
    const url = this._buildEndpoint({ path, withHost });
    return universalFetch(url, {
      method: "POST",
      headers: this._buildHeaders({ extraHeaders, skipCommonHeader: true }),
      body: formData,
    }).then(async (response) => {
      if (response.status === 204) {
        return null;
      }

      if (`${response.status}`[0] === "2") {
        return response.json();
      }

      let error = await response.json();

      // 全域錯誤處理
      if (this.enableGlobalErrorHandling) {
        handleGlobalApiError(error, response.status, path);
      }

      return Promise.reject({ status: response.status, ...error });
    });
  }

  formPut({ path, extraHeaders = {}, formData, withHost = false }) {
    const url = this._buildEndpoint({ path, withHost });
    return universalFetch(url, {
      method: "PUT",
      headers: this._buildHeaders({ extraHeaders, skipCommonHeader: true }),
      body: formData,
    }).then(async (response) => {
      if (response.status === 204) {
        return null;
      }

      if (`${response.status}`[0] === "2") {
        return response.json();
      }

      let error = await response.json();

      // 全域錯誤處理
      if (this.enableGlobalErrorHandling) {
        handleGlobalApiError(error, response.status, path);
      }

      return Promise.reject({ status: response.status, ...error });
    });
  }

  getImage(imageUrl) {
    return this._buildEndpoint({ path: imageUrl });
  }
}

module.exports = _ApiProxy;
